import {
    BxfDynamicPositionEnum,
    BxfLink,
    BxfLinkSizeEnum,
    BxfLinkStyleEnum,
    BxfPopover,
    BxfPopoverBackgroundColor,
} from '@blk/bxf-react-components';
import styled from '@emotion/styled';
import { useMemo } from 'react';

import { InfoPopoverConstants } from '../constants';
import { useInfoPopoverBuilder } from '../hooks';
import { ChatBlkQuery } from '../types';

interface InfoPopoverProps {
    target: string;
    content: string;
    subText: string;
    query?: ChatBlkQuery;
}

const {
    INFO_POPOVER_CONTENT,
    INFO_POPOVER_TARGET,
    DATA_CY_INFO_POPOVER_CONTENT,
    DATA_CY_INFO_POPOVER_TARGET,
} = InfoPopoverConstants;

const InfoPopoverContainer = styled.div`
    display: block;
`;

const InfoPopoverContent = styled.span`
    font-size: 14px;
    line-height: 20px;
    font-weight: normal;
    box-sizing: border-box;
    max-height: 240px;
    overflow-y: auto;
    display: block;
`;

const renderSubText = (subText: string): string => {
    if (subText) {
        return `<span class='server-message-response-content--cell'>${subText}</span>`;
    }
    return '';
};

/**
 * Component that renders an info popover.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.target - The label for the target slot.
 * @param {string} props.content - The content for the popover.
 * @param {string} props.subText - The text to place below target.
 * @param {Object} props.query - The query related to the data.
 *
 * @returns {JSX.Element} The rendered InfoPopover component.
 */
const InfoPopover = ({
    target,
    content,
    subText,
    query,
}: InfoPopoverProps): JSX.Element => {
    const { trackInfoPopoverOpenedTelemetry, trackInfoPopoverClosedTelemetry } =
        useInfoPopoverBuilder(target, query);

    const memoizedPopover = useMemo(
        () =>
            content ? (
                <InfoPopoverContainer>
                    <BxfPopover
                        data-cy={DATA_CY_INFO_POPOVER_CONTENT}
                        overridePlacement={BxfDynamicPositionEnum.TOP_LEFT}
                        backgroundColor={BxfPopoverBackgroundColor.GRAY_10}
                        dataLinkEvent='chatblk | result | footnote | close'
                        hasIcon={false}
                        hasCloseIcon={false}
                        customPadding={12}
                        isChatCanvasOverlay
                        shouldPopoverScroll
                        shouldCloseOnScroll
                        onPopoverOpened={trackInfoPopoverOpenedTelemetry}
                        onPopoverClosed={trackInfoPopoverClosedTelemetry}
                    >
                        <div slot={INFO_POPOVER_TARGET}>
                            <BxfLink
                                data-cy={DATA_CY_INFO_POPOVER_TARGET}
                                label={target}
                                dataLinkEvent='chatblk | result | footnote | open'
                                size={BxfLinkSizeEnum.MEDIUM}
                                linkStyle={BxfLinkStyleEnum.DASHED}
                                underlineOffset='4px'
                            />
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: `${renderSubText(subText)}`,
                                }}
                            ></div>
                        </div>
                        <InfoPopoverContent slot={INFO_POPOVER_CONTENT}>
                            {content}
                        </InfoPopoverContent>
                    </BxfPopover>
                </InfoPopoverContainer>
            ) : (
                <div
                    dangerouslySetInnerHTML={{
                        __html: `${target}${renderSubText(subText)}`,
                    }}
                ></div>
            ),
        [target, content, query]
    );
    return memoizedPopover;
};

export default InfoPopover;
